/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { Button, Page, Skeleton } from '../components/console/ui';
import ProjectsHeader from '../components/projects/ProjectsHeader';
import AuditLogTable, { AUDIT_ACTIONS, auditResourceOptions, resourceLabel } from '../components/projects/AuditLogTable';
import { auditLogService } from '../services/api';
import { AuditLog, Project } from '../types';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { useAuditLogs } from '../hooks/useAuditLogs';

type Range = '7' | '30' | '90' | 'all';
const RANGE_LABEL: Record<Range, string> = {
  '7': 'Last 7 days',
  '30': 'Last 30 days',
  '90': 'Last 90 days',
  all: 'All time',
};

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const AuditLogs: React.FC = () => {
  const { projects } = useSelectedProject();
  const projectList = projects as Project[];

  // Starts with every project - no need to pick one first.
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [range, setRange] = useState<Range>('7');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [liveLogs, setLiveLogs] = useState<AuditLog[]>([]);

  const projectNames = useMemo(
    () => Object.fromEntries(projectList.map((p) => [p.id, p.name])) as Record<string, string>,
    [projectList],
  );
  const allIds = useMemo(() => projectList.map((p) => p.id).join(','), [projectList]);
  const scopeIds = useMemo(
    () => (projectFilter === 'all' ? (allIds ? allIds.split(',') : []) : [projectFilter]),
    [projectFilter, allIds],
  );
  const scopeKey = scopeIds.join(',');

  const { logs: historicalLogs, isLoading, isFetching, refetch } = useAuditLogs({
    projectIds: scopeIds,
    action: actionFilter,
    resourceType: resourceTypeFilter,
    pageSize: 100,
  });

  // Live updates: one stream per project in scope.
  useEffect(() => {
    setLiveLogs([]);
    if (!isLiveMode || !scopeKey) return undefined;
    const sockets = new Map<string, WebSocket>();
    const timers: ReturnType<typeof setTimeout>[] = [];
    let disposed = false;

    const connect = (projectId: string) => {
      if (disposed) return;
      const ws = new WebSocket(auditLogService.getStreamUrl(projectId));
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'audit_log' && data.data) {
            setLiveLogs((prev) => [data.data as AuditLog, ...prev].slice(0, 50));
          }
        } catch (error) {
          console.error('Failed to parse audit log message:', error);
        }
      };
      ws.onclose = () => {
        if (!disposed) timers.push(setTimeout(() => connect(projectId), 3000));
      };
      sockets.set(projectId, ws);
    };

    scopeKey.split(',').forEach(connect);
    return () => {
      disposed = true;
      timers.forEach(clearTimeout);
      sockets.forEach((ws) => ws.close());
    };
  }, [isLiveMode, scopeKey]);

  const liveSet = useMemo(() => new Set(liveLogs), [liveLogs]);

  const filteredLogs = useMemo(() => {
    const seen = new Set<string>();
    const merged = (isLiveMode ? [...liveLogs, ...historicalLogs] : historicalLogs).filter((log) => {
      if (!log.id) return true;
      if (seen.has(log.id)) return false;
      seen.add(log.id);
      return true;
    });
    const since = range === 'all' ? 0 : Date.now() - Number(range) * 86400000;
    const q = searchQuery.trim().toLowerCase();
    return merged.filter((log) => {
      if (since && new Date(log.created_at).getTime() < since) return false;
      if (actionFilter !== 'all' && log.action !== actionFilter) return false;
      if (resourceTypeFilter !== 'all' && log.resource_type !== resourceTypeFilter) return false;
      if (!q) return true;
      return (
        log.user_email?.toLowerCase().includes(q) ||
        log.resource_name?.toLowerCase().includes(q) ||
        log.action?.toLowerCase().includes(q) ||
        log.resource_type?.toLowerCase().includes(q)
      );
    });
  }, [isLiveMode, liveLogs, historicalLogs, range, actionFilter, resourceTypeFilter, searchQuery]);

  const exportCsv = () => {
    if (filteredLogs.length === 0) {
      toast.error('Nothing to export for these filters');
      return;
    }
    const header = ['Time', 'User', 'Action', 'Resource type', 'Resource', 'Project', 'IP'];
    const rows = filteredLogs.map((l) => [
      l.created_at,
      l.user_email,
      l.action,
      resourceLabel(l.resource_type),
      l.resource_name,
      projectNames[l.project_id] || l.project_id,
      l.ip_address,
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filtersActive = actionFilter !== 'all' || resourceTypeFilter !== 'all' || !!searchQuery.trim();
  const emptyMessage = filtersActive
    ? 'No activity matches these filters.'
    : range === 'all'
      ? 'No activity recorded yet. Creating, starting or deleting resources will show up here.'
      : `No activity in the ${RANGE_LABEL[range].toLowerCase()}. Try a wider date range.`;

  return (
    <Page>
      <ProjectsHeader
        subtitle="Every create, start, stop and delete across your projects."
        projectCount={projectList.length}
        actions={
          <>
            <label className="inline-flex items-center gap-2.5 text-sm min-h-[40px] cursor-pointer text-ps-text">
              <input
                type="checkbox"
                className="w-[18px] h-[18px] accent-[#F97316]"
                checked={isLiveMode}
                onChange={(e) => setIsLiveMode(e.target.checked)}
                disabled={scopeIds.length === 0}
              />
              Live updates
              {isLiveMode && <span className="w-2 h-2 rounded-full bg-ps-ok animate-pulse" aria-hidden="true" />}
            </label>
            <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
            <Button variant="secondary" onClick={exportCsv}>
              Export CSV
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap gap-2.5">
        <label className="sr-only" htmlFor="al-project">
          Project
        </label>
        <select id="al-project" className="ps-input w-auto" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
          <option value="all">All projects</option>
          {projectList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="al-range">
          Date range
        </label>
        <select id="al-range" className="ps-input w-auto" value={range} onChange={(e) => setRange(e.target.value as Range)}>
          {(Object.keys(RANGE_LABEL) as Range[]).map((r) => (
            <option key={r} value={r}>
              {RANGE_LABEL[r]}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="al-action">
          Action
        </label>
        <select id="al-action" className="ps-input w-auto" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          <option value="all">All actions</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="al-resource">
          Resource
        </label>
        <select
          id="al-resource"
          className="ps-input w-auto"
          value={resourceTypeFilter}
          onChange={(e) => setResourceTypeFilter(e.target.value)}
        >
          <option value="all">All resources</option>
          {auditResourceOptions().map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="al-search">
          Search
        </label>
        <input
          id="al-search"
          className="ps-input flex-[1_1_220px] w-auto"
          placeholder="Search user or resource"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {isLiveMode && (
        <p className="m-0 text-sm text-ps-ok" role="status">
          Live updates on - new events appear at the top as they happen.
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : (
        <AuditLogTable
          logs={filteredLogs}
          projectNames={projectNames}
          showProject={projectFilter === 'all'}
          highlight={isLiveMode ? liveSet : undefined}
          emptyMessage={projectList.length === 0 ? 'Create a project to start recording activity.' : emptyMessage}
        />
      )}
      {filteredLogs.length > 0 && (
        <p className="m-0 text-ps-subtle text-[13px]">
          Showing {filteredLogs.length} event{filteredLogs.length === 1 ? '' : 's'} - the latest 100 per project.
        </p>
      )}
    </Page>
  );
};

export default AuditLogs;
